import React, { useState, useEffect, useCallback } from 'react';
import { getExcludedModels, saveExcludedModels, saveLastDateRange } from '../../utils/storage';
import { useCurrencyManager } from '../../hooks/useCurrencyManager';
import { UsageCharts } from '../UsageCharts';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { UsageDashboardProps } from './types';
import { SummarySection } from './components/SummarySection';
import { ModelUsageSection } from './components/ModelUsageSection';
import { FilterBar } from './components/FilterBar';
import { LoadingModal } from './components/LoadingModal';
import { ApiKeyTable } from './components/ApiKeyTable';
import { DashboardHeader } from './components/DashboardHeader';
import { ErrorDisplay } from './components/ErrorDisplay';
import { EmptyState } from './components/EmptyState';
import { ReloadingOverlay, LoadingBanner, PartialErrorBanner } from './components/StatusBanners';
import { useURLState } from './hooks/useURLState';
import { useDataSource } from './hooks/useDataSource';
import { useMetadata } from './hooks/useMetadata';
import { useFetchUsage } from './hooks/useFetchUsage';
import { useFilteredData } from './hooks/useFilteredData';
import { useSortedPaginatedData } from './hooks/useSortedPaginatedData';
import { useExportReport } from './hooks/useExportReport';
import { useClickOutside } from './hooks/useClickOutside';
import { DateRange } from '../../constants/ui';
import { parseCustomUtcRange } from '../../utils/dateUtils';

const UsageDashboard: React.FC<UsageDashboardProps> = ({
  className = '',
  hasOpenAiKey: hasOpenAiKeyProp,
  hasAnthropicKey: hasAnthropicKeyProp,
  credentialRevision,
  timezoneMode,
  onToggleTimezone
}) => {
  // ── URL-synced state ────────────────────────────────
  const {
    filter, setFilter,
    sort, setSort,
    dateRange, setDateRange,
    customDateRange, setCustomDateRange,
    selectedWorkspace, setSelectedWorkspace,
    selectedModel, setSelectedModel,
    getDateRange, updateURL,
  } = useURLState();

  // ── Data source ─────────────────────────────────────
  const {
    dataSource, setDataSource,
    resolvedHasOpenAiKey, resolvedHasAnthropicKey,
    sourceDisplayName, hasBothProviders, includesAnthropic,
  } = useDataSource({ hasOpenAiKeyProp, hasAnthropicKeyProp });

  // ── Employee filter states ──────────────────────────
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [employeeNameFilter, setEmployeeNameFilter] = useState<string>('');

  // ── Metadata (labels, employees, org) ───────────────
  const {
    labelDefinitions, setLabelDefinitions,
    apiKeyMetadata, setApiKeyMetadata,
    employeeData,
    headquarterOptions, departmentOptions,
    headquarterSelectValue, departmentSelectValue,
    activeHeadquarter, isDepartmentSelectionDisabled,
    normalizedDepartmentFilter,
  } = useMetadata(selectedDepartment, selectedRole);

  // ── Excluded models ─────────────────────────────────
  const [excludedModels, setExcludedModels] = useState<string[]>(getExcludedModels());
  const [showExcludeModelsDropdown, setShowExcludeModelsDropdown] = useState(false);

  // ── Workspace / model dropdowns ─────────────────────
  const [showWorkspaceDropdown, setShowWorkspaceDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);

  // ── Expanded row ────────────────────────────────────
  const [expandedApiKey, setExpandedApiKey] = useState<string | null>(null);
  const [showModelUsage, setShowModelUsage] = useState(true);

  // ── Model sort state ────────────────────────────────
  const [modelSortKey, setModelSortKey] = useState<string>('cost');
  const [modelSortDir, setModelSortDir] = useState<'asc' | 'desc'>('desc');

  const handleModelSort = useCallback((key: string) => {
    setModelSortKey(prev => {
      if (prev === key) {
        setModelSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return key;
      }
      setModelSortDir(key === 'model' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  // ── Currency ────────────────────────────────────────
  const { currency, exchangeRate, isLoadingRate, formatAmountSync } = useCurrencyManager();

  // ── Fetch usage hook ────────────────────────────────
  const {
    data, loading, isReloading, error, progress,
    showLoadingModal, setShowLoadingModal,
    loadingProgress,
    setIsBackgroundLoading, fetchUsageData,
  } = useFetchUsage({
    dataSource,
    resolvedHasOpenAiKey,
    resolvedHasAnthropicKey,
    credentialRevision,
    getDateRange,
    dateRange,
    customDateRange,
    setLabelDefinitions,
    setApiKeyMetadata,
    setPageSize: () => {}, // page size is managed by useSortedPaginatedData
  });

  // ── Filtered data hook ──────────────────────────────
  const {
    filteredData, sortedModelUsage,
    dataWindow, cacheTimestamp, coverageInfo,
    availableWorkspaces, availableModels,
    findEmployeeForApiKey,
  } = useFilteredData({
    data, getDateRange, excludedModels,
    selectedWorkspace, selectedModel, filter,
    selectedDepartment, normalizedDepartmentFilter,
    selectedRole, employeeNameFilter, employeeData,
    dataSource, modelSortKey, modelSortDir,
  });

  // ── Sorted & paginated data hook ────────────────────
  const {
    filteredSortedApiKeys, paginatedApiKeys,
    currentOffset, setCurrentOffset,
    pageSize, handleSort,
  } = useSortedPaginatedData({
    data, filteredData, filter, sort, setSort,
    selectedWorkspace, selectedModel,
    apiKeyMetadata, labelDefinitions,
    selectedDepartment, normalizedDepartmentFilter,
    selectedRole, employeeNameFilter, employeeData,
    updateURL, dateRange, customDateRange,
  });

  // ── Export report hook ──────────────────────────────
  const { pendingExportRange, handleExportHtmlReport } = useExportReport({
    data, filteredData, employeeData, findEmployeeForApiKey,
    getDateRange, dataSource, resolvedHasAnthropicKey,
    loading, customDateRange, dateRange,
    setDateRange, setCustomDateRange,
  });

  // ── High cost alerts hook ───────────────────────────
  // ── Click outside handler ───────────────────────────
  useClickOutside({
    setShowWorkspaceDropdown,
    setShowModelDropdown,
    setShowExcludeModelsDropdown,
  });

  // ── Date range callbacks (pre-bound for DashboardHeader) ──
  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setCustomDateRange(null);
    updateURL(filter, sort, range, null, selectedWorkspace, selectedModel);
    saveLastDateRange(range);
  }, [filter, sort, selectedWorkspace, selectedModel, updateURL, setDateRange, setCustomDateRange]);

  const handleCustomRangeApply = useCallback((startDateStr: string, endDateStr: string) => {
    const customRange = parseCustomUtcRange(startDateStr, endDateStr);
    if (!customRange) return;
    setCustomDateRange(customRange);
    updateURL(filter, sort, dateRange, customRange, selectedWorkspace, selectedModel);
    saveLastDateRange('custom', { startDate: startDateStr, endDate: endDateStr });
  }, [filter, sort, dateRange, selectedWorkspace, selectedModel, updateURL, setCustomDateRange]);

  // ── Workspace / model availability guards ───────────
  useEffect(() => {
    if (selectedWorkspace !== 'all' && availableWorkspaces.length > 0) {
      const isAvailable = selectedWorkspace.startsWith('wsname:')
        ? availableWorkspaces.some(w => w.name === selectedWorkspace.slice(7))
        : availableWorkspaces.some(w => w.id === selectedWorkspace);
      if (!isAvailable) {
        setSelectedWorkspace('all');
        updateURL(filter, sort, dateRange, customDateRange, 'all', selectedModel);
      }
    }
  }, [availableWorkspaces, selectedWorkspace, filter, sort, dateRange, customDateRange, updateURL, selectedModel, setSelectedWorkspace]);

  useEffect(() => {
    if (selectedModel !== 'all' && !availableModels.includes(selectedModel)) {
      setSelectedModel('all');
      updateURL(filter, sort, dateRange, customDateRange, selectedWorkspace, 'all');
    }
  }, [availableModels, selectedModel, filter, sort, dateRange, customDateRange, selectedWorkspace, updateURL, setSelectedModel]);

  // ── RENDER ──────────────────────────────────────────

  if (loading && !data) {
    return (
      <LoadingSpinner
        message={progress || 'Fetching usage data...'}
        size="sm"
        layout="top-toast"
        className={className}
      />
    );
  }

  if (error && !data) {
    return (
      <ErrorDisplay
        error={error}
        dataSource={dataSource}
        className={className}
        onRetry={() => fetchUsageData(false)}
      />
    );
  }

  if (!data) {
    return (
      <EmptyState
        sourceDisplayName={sourceDisplayName}
        className={className}
        onFetch={() => fetchUsageData(false)}
      />
    );
  }

  return (
    <div className={`space-y-3 ${className} relative`}>
      {isReloading && <ReloadingOverlay progress={progress} />}

      <DashboardHeader
        dataSource={dataSource}
        setDataSource={setDataSource}
        hasBothProviders={hasBothProviders}
        resolvedHasOpenAiKey={resolvedHasOpenAiKey}
        resolvedHasAnthropicKey={resolvedHasAnthropicKey}
        sourceDisplayName={sourceDisplayName}
        dateRange={dateRange}
        customDateRange={customDateRange}
        getDateRange={getDateRange}
        onDateRangeChange={handleDateRangeChange}
        onCustomRangeApply={handleCustomRangeApply}
        filteredData={filteredData}
        isCached={!!data?.cached}
        dataWindow={dataWindow}
        coverageInfo={coverageInfo}
        cacheTimestamp={cacheTimestamp}
        currency={currency}
        loading={loading}
        isReloading={isReloading}
        fetchUsageData={fetchUsageData}
        timezoneMode={timezoneMode}
        onToggleTimezone={onToggleTimezone}
      />

      {loading && <LoadingBanner progress={progress} />}

      {error && data && (
        <PartialErrorBanner error={error} onRetry={() => fetchUsageData(false)} />
      )}

      {!error && data?.partialErrors && data.partialErrors.length > 0 && (
        <PartialErrorBanner
          error={data.partialErrors.join(' • ')}
          onRetry={() => fetchUsageData(false)}
        />
      )}

      <SummarySection filteredData={filteredData} dateRange={dateRange} />

      {filteredData?.dailyUsage && filteredData.dailyUsage.length > 0 && (
        <UsageCharts
          dailyData={filteredData.dailyUsage}
          currency={currency}
          exchangeRate={exchangeRate}
          isLoadingRate={isLoadingRate}
          dateRange={dateRange}
          timezoneMode={timezoneMode}
        />
      )}

      <ModelUsageSection
        showModelUsage={showModelUsage}
        setShowModelUsage={setShowModelUsage}
        includesAnthropic={includesAnthropic}
        sortedModelUsage={sortedModelUsage}
        modelSortKey={modelSortKey}
        modelSortDir={modelSortDir}
        handleModelSort={handleModelSort}
      />

      <FilterBar
        workspaceFilter={{
          selectedWorkspace,
          setSelectedWorkspace,
          showWorkspaceDropdown,
          setShowWorkspaceDropdown,
          availableWorkspaces,
        }}
        modelFilter={{
          selectedModel,
          setSelectedModel,
          showModelDropdown,
          setShowModelDropdown,
          availableModels,
        }}
        apiKeyFilter={{
          filter,
          setFilter,
        }}
        excludedModelFilter={{
          excludedModels,
          setExcludedModels,
          showExcludeModelsDropdown,
          setShowExcludeModelsDropdown,
          allUsageModels: data?.modelUsage.map((model) => model.model) || [],
          saveExcludedModels,
        }}
        employeeFilter={{
          employeeData,
          selectedDepartment,
          setSelectedDepartment,
          selectedRole,
          setSelectedRole,
          employeeNameFilter,
          setEmployeeNameFilter,
          headquarterSelectValue,
          departmentSelectValue,
          activeHeadquarter,
          isDepartmentSelectionDisabled,
          normalizedDepartmentFilter,
          headquarterOptions,
          departmentOptions,
        }}
        exportControl={{
          handleExportHtmlReport,
          pendingExportRange,
          data,
        }}
        urlSync={{
          updateURL,
          sort,
          dateRange,
          customDateRange,
        }}
      />

      <ApiKeyTable
        filteredData={filteredData}
        data={data}
        filteredSortedApiKeys={filteredSortedApiKeys}
        paginatedApiKeys={paginatedApiKeys}
        currentOffset={currentOffset}
        setCurrentOffset={setCurrentOffset}
        pageSize={pageSize}
        loading={loading}
        expandedApiKey={expandedApiKey}
        setExpandedApiKey={setExpandedApiKey}
        sort={sort}
        handleSort={handleSort}
        labelDefinitions={labelDefinitions}
        setLabelDefinitions={setLabelDefinitions}
        apiKeyMetadata={apiKeyMetadata}
        setApiKeyMetadata={setApiKeyMetadata}
        employeeData={employeeData}
        findEmployeeForApiKey={findEmployeeForApiKey}
        dateRange={dateRange}
        dataSource={dataSource}
        includesAnthropic={includesAnthropic}
        normalizedDepartmentFilter={normalizedDepartmentFilter}
        selectedRole={selectedRole}
        formatAmountSync={formatAmountSync}
      />

      <LoadingModal
        showLoadingModal={showLoadingModal}
        loadingProgress={loadingProgress}
        setShowLoadingModal={setShowLoadingModal}
        setIsBackgroundLoading={setIsBackgroundLoading}
      />
    </div>
  );
};

export default UsageDashboard;
